/**
 * Contas a pagar e receber.
 *
 * A situação de cada título é PROJEÇÃO das baixas — não há botão "marcar como
 * pago". Quem quita, dá baixa; quem errou, estorna com lançamento novo. A
 * trilha mostra os dois.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../api.ts";

interface Conta {
  id: string; tipo: string; descricao: string; parceiro: string;
  parcela: number; total_parcelas: number;
  valor_original: string; valor_liquidado: string; saldo: string;
  vencimento: string; situacao: string; dias_atraso: number;
}

interface Baixa {
  id: string; data: string; valor: string; forma: string;
  juros: string; desconto: string; observacao: string | null;
}

interface Posicao {
  a_receber: string; a_pagar: string;
  receber_vencido: string; pagar_vencido: string; titulos_vencidos: string;
}

const n2 = (v: string) => Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dt = (v: string) => new Date(v).toLocaleDateString("pt-BR");

function pilSituacao(s: string, atraso: number) {
  if (s === "CANCELADA") return <span className="pil p-info"><i />cancelada</span>;
  if (s === "LIQUIDADA") return <span className="pil p-ok"><i />liquidada</span>;
  if (atraso > 0) return <span className="pil p-erro"><i />{atraso}d em atraso</span>;
  if (s === "PARCIAL") return <span className="pil p-aten"><i />parcial</span>;
  return <span className="pil p-info"><i />aberta</span>;
}

export function Financeiro({ aviso }: { aviso: (t: string) => void }) {
  const [pos, setPos] = useState<Posicao | null>(null);
  const [contas, setContas] = useState<Conta[] | null>(null);
  const [tipo, setTipo] = useState("RECEBER");
  const [situacao, setSituacao] = useState("");
  const [aberta, setAberta] = useState<string | null>(null);

  const recarregar = useCallback(() => {
    setContas(null);
    const q = new URLSearchParams();
    if (tipo) q.set("tipo", tipo);
    if (situacao) q.set("situacao", situacao);
    void api.get<Conta[]>(`/financeiro/contas?${q}`).then(setContas).catch(() => setContas([]));
    void api.get<Posicao>("/financeiro/posicao").then(setPos).catch(() => {});
  }, [tipo, situacao]);

  useEffect(recarregar, [recarregar]);

  return (
    <>
      <div className="cab-doc">
        <h1>Financeiro</h1>
        <select className="campo-texto foco-claro" style={{ width: "auto" }}
          value={tipo} onChange={(e) => setTipo(e.target.value)} aria-label="Tipo">
          <option value="RECEBER">a receber</option>
          <option value="PAGAR">a pagar</option>
        </select>
        <select className="campo-texto foco-claro" style={{ width: "auto" }}
          value={situacao} onChange={(e) => setSituacao(e.target.value)} aria-label="Situação">
          <option value="">todas</option>
          <option value="ABERTA">abertas</option>
          <option value="PARCIAL">parciais</option>
          <option value="LIQUIDADA">liquidadas</option>
        </select>
        <span className="cod">{contas?.length ?? 0} títulos</span>
      </div>

      {/* Métricas-herói: é o número que o dono olha primeiro (design §4) */}
      <div className="metricas">
        <div>
          <span className="rotulo">A receber</span>
          <span className="vl">R$ {pos ? n2(pos.a_receber) : "—"}</span>
          <span className="ct">em aberto</span>
        </div>
        <div>
          <span className="rotulo">A pagar</span>
          <span className="vl">R$ {pos ? n2(pos.a_pagar) : "—"}</span>
          <span className="ct">em aberto</span>
        </div>
        <div>
          <span className="rotulo">Vencido</span>
          <span className="vl" style={{
            color: pos && Number(pos.receber_vencido) > 0 ? "var(--erro-texto)" : undefined,
          }}>
            R$ {pos ? n2(pos.receber_vencido) : "—"}
          </span>
          <span className="ct">a receber</span>
        </div>
        <div>
          <span className="rotulo">Títulos vencidos</span>
          <span className="vl">{pos?.titulos_vencidos ?? "—"}</span>
          <span className="ct">exigem cobrança</span>
        </div>
        <div>
          <span className="rotulo">Posição líquida</span>
          <span className="vl">
            R$ {pos ? n2(String(Number(pos.a_receber) - Number(pos.a_pagar))) : "—"}
          </span>
          <span className="ct">receber − pagar</span>
        </div>
      </div>

      <div className="rolagem">
        <div className="painel">
          {contas === null ? (
            <Esq />
          ) : contas.length === 0 ? (
            <div className="vazio"><p>Nenhum título com esses filtros.</p></div>
          ) : (
            <table className="tab">
              <thead>
                <tr>
                  <th>Descrição</th>
                  <th>Parceiro</th>
                  <th>Vencimento</th>
                  <th style={{ textAlign: "right" }}>Valor</th>
                  <th style={{ textAlign: "right" }}>Liquidado</th>
                  <th style={{ textAlign: "right" }}>Saldo</th>
                  <th>Situação</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {contas.map((c) => (
                  <tr key={c.id}>
                    <td>{c.descricao}</td>
                    <td>{c.parceiro}</td>
                    <td className="mono">{dt(c.vencimento)}</td>
                    <td className="r">{n2(c.valor_original)}</td>
                    <td className="r">{n2(c.valor_liquidado)}</td>
                    <td className="r" style={{ fontWeight: 600 }}>{n2(c.saldo)}</td>
                    <td>{pilSituacao(c.situacao, Number(c.dias_atraso))}</td>
                    <td style={{ textAlign: "right" }}>
                      <button className="bt bt-neutro" style={{ padding: "3px 9px", fontSize: 12 }}
                        onClick={() => setAberta(aberta === c.id ? null : c.id)}>
                        {aberta === c.id ? "fechar" : "abrir"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {aberta && <DetalheConta id={aberta} aviso={aviso} aoMudar={recarregar} />}
      </div>
    </>
  );
}

const FORMAS = ["PIX", "BOLETO", "DINHEIRO", "TRANSFERENCIA", "CARTAO", "CHEQUE", "OUTRO"];

function DetalheConta({
  id, aviso, aoMudar,
}: { id: string; aviso: (t: string) => void; aoMudar: () => void }) {
  const [c, setC] = useState<(Conta & { baixas: Baixa[] }) | null>(null);
  const [valor, setValor] = useState("");
  const [forma, setForma] = useState("PIX");
  const [ocupado, setOcupado] = useState(false);

  const carregar = useCallback(() => {
    setC(null);
    void api.get<Conta & { baixas: Baixa[] }>(`/financeiro/contas/${id}`)
      .then((r) => { setC(r); setValor(Number(r.saldo) > 0 ? r.saldo : ""); })
      .catch(() => setC(null));
  }, [id]);
  useEffect(carregar, [carregar]);

  if (!c) return <div className="painel"><Esq /></div>;

  async function baixar() {
    setOcupado(true);
    try {
      const r = await api.post<{ situacao: string; saldo: string }>(
        `/financeiro/contas/${id}/baixar`, { valor, forma },
      );
      aviso(`Baixa registrada · ${r.situacao.toLowerCase()} · saldo R$ ${n2(r.saldo)}`);
      carregar(); aoMudar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha na baixa");
    } finally { setOcupado(false); }
  }

  async function estornar(baixaId: string) {
    const motivo = window.prompt("Motivo do estorno (mín. 5 caracteres):");
    if (!motivo) return;
    try {
      await api.post(`/financeiro/baixas/${baixaId}/estornar`, { motivo });
      // Lançamento NOVO com valor negativo: a baixa original continua visível.
      aviso("Estorno lançado · a baixa original continua na trilha");
      carregar(); aoMudar();
    } catch (e) {
      aviso(e instanceof Error ? e.message : "falha ao estornar");
    }
  }

  return (
    <div className="painel">
      <div className="tt" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span className="rotulo">{c.descricao}</span>
        {pilSituacao(c.situacao, Number(c.dias_atraso))}
        <span className="cod">
          {c.parceiro} · vence {dt(c.vencimento)} · saldo R$ {n2(c.saldo)}
        </span>
      </div>

      <table className="tab">
        <thead>
          <tr>
            <th>Data</th><th>Forma</th>
            <th style={{ textAlign: "right" }}>Valor</th>
            <th>Observação</th><th />
          </tr>
        </thead>
        <tbody>
          {c.baixas.length === 0 ? (
            <tr><td colSpan={5} style={{ color: "var(--texto-terciario)" }}>Nenhuma baixa ainda.</td></tr>
          ) : c.baixas.map((b) => {
            const estorno = Number(b.valor) < 0;
            return (
              <tr key={b.id}>
                <td className="mono">{dt(b.data)}</td>
                <td>{b.forma.toLowerCase()}</td>
                <td className="r" style={{ color: estorno ? "var(--erro-texto)" : undefined, fontWeight: 600 }}>
                  {n2(b.valor)}
                </td>
                <td style={{ color: "var(--texto-secundario)" }}>
                  {estorno ? <span className="pil p-erro"><i />estorno</span> : null} {b.observacao ?? ""}
                </td>
                <td style={{ textAlign: "right" }}>
                  {!estorno && (
                    <button className="bt bt-neutro" style={{ padding: "3px 9px", fontSize: 12 }}
                      onClick={() => void estornar(b.id)}>
                      estornar
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {c.situacao !== "CANCELADA" && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", padding: 12, borderTop: "1px solid var(--linha-grade)" }}>
          <label>
            <span className="rotulo" style={{ display: "block", marginBottom: 4 }}>Valor</span>
            <input className="campo-texto num" style={{ width: 140 }} inputMode="decimal"
              value={valor} onChange={(e) => setValor(e.target.value)} />
          </label>
          <label>
            <span className="rotulo" style={{ display: "block", marginBottom: 4 }}>Forma</span>
            <select className="campo-texto foco-claro" value={forma} onChange={(e) => setForma(e.target.value)}>
              {FORMAS.map((f) => <option key={f} value={f}>{f.toLowerCase()}</option>)}
            </select>
          </label>
          <button className="bt" onClick={() => void baixar()} disabled={ocupado || !valor}>
            Dar baixa
          </button>
          <span className="cod" style={{ marginLeft: "auto", maxWidth: 340 }}>
            Baixa parcial e pagamento a maior são aceitos — os dois acontecem na vida real.
          </span>
        </div>
      )}
    </div>
  );
}

function Esq() {
  return (
    <div className="esq">
      {[0, 1, 2, 3].map((i) => (
        <div className="l" key={i}>
          <span className="b" /><span className="b" /><span className="b" /><span className="b" />
        </div>
      ))}
    </div>
  );
}
